"use client";

import Script from "next/script";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

type GoogleAuthButtonProps = {
  onCredential: (
    credential: string,
  ) => Promise<void>;
  disabled?: boolean;
  className?: string;
};

const clientId =
  process.env.NEXT_PUBLIC_GOOGLE_OAUTH_CLIENT_ID ?? "";

type GoogleIdClient = NonNullable<Window["google"]>["accounts"]["id"];
let initializedClient: GoogleIdClient | null = null;
let activeCallback: { current: GoogleAuthButtonProps["onCredential"] } | null = null;

function googleIsAvailable(): boolean {
  return Boolean(
    typeof window !== "undefined" &&
      window.google?.accounts?.id,
  );
}

export default function GoogleAuthButton({
  onCredential,
  disabled = false,
  className = "",
}: GoogleAuthButtonProps) {
  const containerRef =
    useRef<HTMLDivElement>(null);

  const callbackRef =
    useRef(onCredential);

  const [scriptReady, setScriptReady] =
    useState(false);

  const [scriptFailed, setScriptFailed] =
    useState(false);

  useEffect(() => {
    callbackRef.current = onCredential;
  }, [onCredential]);

  useEffect(() => () => {
    if (activeCallback === callbackRef) activeCallback = null;
  }, []);

  useEffect(() => {
    if (googleIsAvailable()) {
      setScriptReady(true);
      return;
    }

    const interval = window.setInterval(() => {
      if (googleIsAvailable()) {
        setScriptReady(true);
        window.clearInterval(interval);
      }
    }, 100);

    const timeout = window.setTimeout(() => {
      window.clearInterval(interval);
    }, 10000);

    return () => {
      window.clearInterval(interval);
      window.clearTimeout(timeout);
    };
  }, []);

  const renderButton = useCallback(() => {
    const container = containerRef.current;
    const google = window.google;

    if (
      !container ||
      !google?.accounts?.id ||
      !clientId
    ) {
      return;
    }

    container.innerHTML = "";

    activeCallback = callbackRef;
    // initialize is page-wide. Submitting a form, re-enabling the button or
    // navigating between login/register must only render/rebind the button.
    if (initializedClient !== google.accounts.id) {
      google.accounts.id.initialize({
        client_id: clientId,
        callback: (response) => {
          if (response.credential) void activeCallback?.current(response.credential);
        },
        auto_select: false,
        cancel_on_tap_outside: true,
      });
      initializedClient = google.accounts.id;
    }

    google.accounts.id.renderButton(
      container,
      {
        type: "standard",
        theme: "outline",
        size: "large",
        text: "continue_with",
        shape: "pill",
        logo_alignment: "left",
        width: 280,
        locale: "pt-BR",
      },
    );
  }, []);

  useEffect(() => {
    if (
      !scriptReady ||
      disabled ||
      scriptFailed
    ) {
      return;
    }

    const frame = window.requestAnimationFrame(
      renderButton,
    );

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [
    scriptReady,
    disabled,
    scriptFailed,
    renderButton,
  ]);

  if (!clientId) {
    return (
      <button
        type="button"
        disabled
        title="Login com Google não configurado"
        className={`flex h-12 w-full cursor-not-allowed items-center justify-center gap-3 rounded-2xl border border-slate-300 bg-white text-sm font-semibold text-slate-400 opacity-70 ${className}`}
      >
        <span className="text-base font-bold">
          G
        </span>

        Continuar com Google
      </button>
    );
  }

  return (
    <>
      <Script
        src="https://accounts.google.com/gsi/client"
        strategy="afterInteractive"
        onReady={() => {
          setScriptFailed(false);
          setScriptReady(true);
        }}
        onError={() => {
          setScriptFailed(true);
        }}
      />

      <div
        className={`relative flex min-h-12 w-full items-center justify-center overflow-hidden rounded-2xl ${
          disabled
            ? "pointer-events-none opacity-60"
            : ""
        } ${className}`}
        aria-busy={
          !scriptReady && !scriptFailed
        }
      >
        <div
          ref={containerRef}
          className="flex w-full justify-center"
        />

        {!scriptReady && !scriptFailed && (
          <div className="absolute inset-0 flex items-center justify-center rounded-2xl border border-slate-300 bg-white text-sm font-semibold text-slate-500">
            Carregando Google...
          </div>
        )}

        {scriptFailed && (
          <div className="absolute inset-0 flex items-center justify-center rounded-2xl border border-red-200 bg-red-50 px-4 text-center text-sm font-semibold text-red-600">
            Não foi possível carregar o Google.
          </div>
        )}
      </div>
    </>
  );
}
